import _ from 'lodash'
import qs from 'qs'

const stringifyQuery = (query = {}) => {
  const sanitisedQuery = _.chain(query)
    .toPairs()
    .map(([ key, value ]) => {
      if (_.isArray(value)) {
        const csvValue = _.chain(value)
          .join(',')
          .value()
        return {
          [key]: csvValue
        }
      } else {
        return {
          [key]: value
        }
      }
    })
    .reduce((memo, next) => ({ ...memo, ...next }), {})
    .value()
  return qs.stringify(sanitisedQuery)
}
class Config {
  config = {
    endpointUrls: {
      searchProducts: () => `/v2/products/search`,
      fetchProduct: ({ id, ...query }) => `/v2/products/${id}/full?${stringifyQuery(query)}`,
      fetchProductGroupProducts: ({ id, ...query }) => `/v2/products/${id}/group-products/full?${stringifyQuery(query)}`,
      fetchProductCatalogues: () => `/v2/products/catalogues`,
      searchOrders: () => `/v2/orders/search`,
      searchCustomers: () => `/v2/customers/search`,
      fetchCustomerDetails: ({ id }) => `/v2/customers/${id}`,
      updateCustomerDetails: ({ id }) => `/v2/customers/${id}`,
      createCustomer: () => `/v2/customers`,
      login: () => '/v1/login',
      resetPassword: () => '/v1/reset-password',
      changePassword: () => '/v1/change-password',
      createUser: () => '/v2/users',
      fetchUserDetails: ({ id, ...query }) => `/v2/users/${id}?${stringifyQuery(query)}`,
      searchUsers: () => `/v2/users/search`,
      fetchRoles: () => '/v1/roles',
      fetchCategories: ({ regionId }) => `/v2/categories?${stringifyQuery({ regionId })}`,
      updateUser: ({ id }) => `/v2/users/${id}`,
      toggleDeactivateUser: ({ id }) => `/v1/users/${id}/toggle-deactivate`,
      changeUserPassword: ({ id }) => `/v1/auth/${id}/change-password`,
      followCustomer: ({ id }) => `/v1/customers/${id}/follow`,
      unfollowCustomer: ({ id }) => `/v1/customers/${id}/unfollow`,
      fetchFollowing: () => `/v1/users/@me/following`,
      fetchCustomerRelationshipsForUser: ({ userId, ...query }) => `/v2/users/${userId}/customer-user-relationships${query ? `?${stringifyQuery(query)}` : ''}`,
      setCustomerRelationshipsForUser: ({ userId }) => `/v2/users/${userId}/customer-user-relationships`,
      setStoreRelationshipsForUser: ({ userId }) => `/v2/users/${userId}/user-stores`,
      setDepartmentRelationshipsForUser: ({ userId }) => `/v2/users/${userId}/user-departments`,
      deleteCustomerUserRelationship: ({ id }) => `/v2/customer-user-relationships/${id}`,
      fetchPersonalNotifications: (query) => `/v1/users/@me/notifications?${stringifyQuery(query)}`,
      fetchNotificationsForUser: ({ id, ...query }) => `/v1/users/${id}/notifications?${stringifyQuery(query)}`,
      setNotificationUserStatus: ({ id }) => `/v1/users/@me/notifications/${id}`,
      fetchActivities: (query) => `/v2/activities${query ? `?${stringifyQuery(query)}` : ''}`,
      fetchStores: (query) => `/v2/stores${query ? `?${stringifyQuery(query)}` : ''}`,
      fetchLatestCustomerActivities: () => `/v1/users/@me/following/activities`,
      getCustomerMessagePreview: (id) => `/v2/message-templates/${id}/preview`,
      sendMessage: () => '/v2/messages',
      fetchMessages: (query) => `/v2/messages${query ? `?${stringifyQuery(query)}` : ''}`,
      createOrder: () => `/v2/orders`,
      fetchOrders: (query) => `/v2/orders${query ? `?${stringifyQuery(query)}` : ''}`,
      updateOrder: ({ id }) => `/v2/orders/${id}`,
      reassignOrder: ({ id }) => `/v2/orders/${id}/reassign`,
      refundOrder: ({ id }) => `/v2/orders/${id}/refund`,
      cancelOrder: ({ id }) => `/v2/orders/${id}/cancel`,
      createBasket: () => `/v2/salesforce-ocapi/baskets`,
      updateBasket: (id) => `/v2/salesforce-ocapi/baskets/${id}`,
      fetchBasketShippingMethods: (id) => `/v2/salesforce-ocapi/baskets/${id}/shipping-methods`,
      fetchBasketPaymentOptions: (id) => `/v2/salesforce-ocapi/baskets/${id}/payment-methods`,
      fetchMessagesByCustomerIdPaged: ({ id, ...query }) => `/v1/customers/${id}/messages?${stringifyQuery(query)}`,
      fetchDeliveryOptions: () => `/v1/delivery-options`,
      fetchMessageTemplates: (query) => `/v2/message-templates${query ? `?${stringifyQuery(query)}` : ''}`,
      setCurrentOrder: (query) => `/v1/set-current-order?${stringifyQuery(query)}`,
      getCurrentOrder: (query) => `/v1/get-current-order?${stringifyQuery(query)}`,
      fetchProductByEan: ({ ean, ...query }) => `/v1/products/ean/${ean}?${stringifyQuery(query)}`,
      fetchVariants: ({ ...query }) => `/v2/variants?${stringifyQuery(query)}`,
      fetchStore: ({ id, ...query }) => `/v2/stores/${id}${!_.isEmpty(query) ? `?${stringifyQuery(query)}` : ''}`,
      createStore: () => `/v2/stores`,
      patchStore: ({ id }) => `/v2/stores/${id}`,
      setStoreCalendars: ({ id }) => `/v2/stores/${id}/store-calendars`,
      // bucketName is optional
      fetchResources: (query) => `/v1/resources?${stringifyQuery(query)}`,
      fetchResource: ({ key }) => `/v1/resources/${key}`,
      fetchOrdersByCustomer: ({ id }) => `/v1/customers/${id}/orders`,
      changeActionedStatus: ({ id }) => `/v1/notifications/${id}`,
      getTotalNotificationsNotViewed: () => `/v1/users/@me/notifications/total`,
      fetchImageUploadUrl: (query) => `/v1/auth/image-upload-url?${stringifyQuery(query)}`,
      validateCustomerToken: ({ token }) => `/v2/customer-token/${token}/validate`,
      unsubscribe: ({ customerId }) => `/v2/customers/${customerId}/unsubscribe`,
      fetchOrder: ({ id, ...query }) => `/v2/orders/${id}?${stringifyQuery(query)}`,
      editOrder: ({ id }) => `/v2/orders/${id}/edit`,
      exchangeOrder: ({ id }) => `/v2/orders/${id}/exchange`,
      fetchVariantStoreStocks: ({ variantId }) => `/v1/variants/${variantId}/storeStocks`,
      anonymiseCustomer: ({ customerId }) => `/v2/customers/${customerId}/anonymize`,
      fetchRegions: () => `/v2/regions`,
      fetchDepartments: () => `/v1/departments`,
      fetchActiveQuestionsForUser: () => `/v1/poll-questions/@me`,
      fetchPollQuestionInstances: (query) => `/v1/poll-questions?${stringifyQuery(query)}`,
      deletePollQuestionInstanceById: ({ id }) => `/v1/poll-questions/${id}`,
      savePollQuestionAnswers: () => `/v1/poll-answers/@me`,
      sendFeedback: () => '/v1/feedback',
      fetchPaypalCredentials: () => `/v1/auth/paypal-credentials`,
      fetchOrderConfig: () => '/v2/orders/config',
      updateOrderStatus: ({ id }) => `/v2/orders/${id}`,
      payOrderByEmail: ({ orderNumber }) => `/v1/orders/pay-by-email/${orderNumber}`,
      searchInspirations: () => `/v2/inspirations/search`,
      fetchInspirationById: ({ id }) => `/v1/inspirations/${id}`,
      deleteInspirationById: ({ id }) => `/v1/inspirations/${id}`,
      fetchAllInspirationTags: (query) => `/v1/inspirations/tags?${stringifyQuery(query)}`,
      fetchAllTags: (query) => `/v1/tags?${stringifyQuery(query)}`,
      setAuthContext: () => `/v1/auth-context`,
      getAuthContext: () => `/v1/auth-context`,
      createScheduledTask: () => `/v1/scheduled-tasks`,
      flagMessage: (query) => `/v1/flag-message?${stringifyQuery(query)}`,
      fetchMessageById: (id, query) => `/v2/messages/${id}${query ? `?${stringifyQuery(query)}` : ''}`,
      searchMessages: () => `/v2/messages/search`,
      fetchFeedbackTypes: () => `/v1/feedback/types`,
      changeInvalidPassword: () => `/v1/auth/@me/change-invalid-password`,
      createTrigger: () => '/v1/triggers',
      fetchProductWaitlist: (query) => `/v1/product-waitlist?${stringifyQuery(query)}`,
      addToProductWaitlist: () => `/v1/product-waitlist`,
      deleteFromProductWaitlist: (id) => `/v1/product-waitlist/${id}`,
      addToCustomerProductWaitlist: ({ customerId, storeId, replace }) => `/v1/customers/${customerId}/product-waitlist?${stringifyQuery({ storeId, replace })}`,
      searchEvents: () => `/v2/events/search`,
      fetchEventById: ({ id }) => `/v1/events/${id}`,
      deleteEventById: ({ id }) => `/v1/events/${id}`,
      fetchAllEventTags: (query) => `/v1/events/tags?${stringifyQuery(query)}`,
      fetchEventWaitlist: (query) => `/v1/event-waitlist?${stringifyQuery(query)}`,
      addToEventWaitlist: () => `/v1/event-waitlist`,
      deleteFromEventWaitlist: (id) => `/v1/event-waitlist/${id}`,
      addToCustomerEventWaitlist: ({ customerId, storeId, replace }) => `/v1/customers/${customerId}/event-waitlist?${stringifyQuery({ storeId, replace })}`,
      token: () => `/v1/token`,
      logout: () => `/v1/logout`,
      fetchCustomerStoreMarketing: (query) => `/v2/customer-store-marketing${query ? `?${stringifyQuery(query)}` : ''}`,
      setCustomerStoreMarketing: ({ customerId }) => `/v2/customers/${customerId}/customer-store-marketing`,
      fetchAppointment: ({ id }) => `/v1/appointment/${id}/secure`,
      fetchAppointmentWithNoCustomerData: ({ id }) => `/v1/appointment/${id}`,
      createAppointment: () => `/v2/appointments`,
      updateAppointment: ({ id }) => `/v2/appointments/${id}`,
      cancelAppointment: ({ id }) => `/v2/appointments/${id}/cancel`,
      fetchVideoToken: ({ id }) => `/v1/appointment/${id}/video-token`,
      fetchAppointments: (query) => `/v2/appointments${query ? `?${stringifyQuery(query)}` : ''}`,
      fetchCalendar: ({ id }) => `/v2/calendars/${id}`,
      fetchCalendars: (query) => `/v2/calendars${query ? `?${stringifyQuery(query)}` : ''}`,
      createCalendar: () => `/v2/calendars`,
      updateCalendar: ({ id }) => `/v2/calendars/${id}`,
      createProductList: () => `/v2/product-lists`,
      fetchProductList: ({ id }) => `/v2/product-lists/${id}`,
      fetchProductLists: (query) => `/v2/product-lists/${query ? `?${stringifyQuery(query)}` : ''}`,
      updateProductList: ({ id }) => `/v2/product-lists/${id}`,
      putProductList: ({ id }) => `/v2/product-lists/${id}`,
      deleteProductList: ({ id }) => `/v2/product-lists/${id}`,
      fetchRemoteConfig: () => `/v2/client-config`,
      fetchChatToken: ({ id }) => `/v1/twilio/chat/${id}/token`,
      deleteChatChannel: ({ id }) => `/v1/twilio/chat/${id}`
    }
  }
  getConfig = () => {
    return this.config
  }
  setConfig = (newConfig) => {
    this.config = {
      ...this.config,
      ...newConfig
    }
  }
}

export default new Config()



// WEBPACK FOOTER //
// ../src/config.js